import { tx } from "../lib/i18n";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Timer, CheckCircle2, XCircle, Wallet, ShieldCheck, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { fmtFull, money, monthLabel } from "../lib/format";
import { Spinner } from "../components/site/Layout";
import { SentooBadge } from "../components/home/HeroSections";

const useCountdown = (iso) => {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!iso) return;
    const tick = () => setLeft(Math.max(0, Math.floor((new Date(iso) - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [iso]);
  return left;
};

const Summary = ({ b, lang }) => (
  <div className="space-y-2 text-sm">
    <div className="flex justify-between"><span className="text-osk-muted">{tx("Les")}</span><span className="font-bold text-right">{b.lesson?.title}</span></div>
    <div className="flex justify-between"><span className="text-osk-muted">{tx("Kind")}</span><span className="font-bold">{b.child?.name}</span></div>
    {b.month && <div className="flex justify-between"><span className="text-osk-muted">{tx("Maand")}</span><span className="font-bold">{monthLabel(b.month, lang)}</span></div>}
    <div className="flex justify-between"><span className="text-osk-muted">{tx("Aantal lessen")}</span><span className="font-bold">{b.session_ids?.length}</span></div>
    {b.sessions?.[0] && <div className="flex justify-between"><span className="text-osk-muted">{tx("Eerste les")}</span><span className="font-bold">{fmtFull(b.sessions[0].start, lang)}</span></div>}
  </div>
);

export const PayPanel = ({ booking, credit, onPay, busy, expired }) => {
  const [useCredit, setUseCredit] = useState(true);
  const applied = useCredit ? Math.min(credit || 0, booking.amount) : 0;
  return (
    <div className="space-y-4">
      {credit > 0 && (
        <label className="flex items-center justify-between rounded-xl bg-osk-light p-3 text-sm">
          <span className="flex items-center gap-2"><Wallet className="h-4 w-4 text-osk-blue" />{tx("Lestegoed gebruiken")} ({money(credit, booking.currency)})</span>
          <input type="checkbox" className="h-4 w-4 accent-[#1565D8]" checked={useCredit} onChange={(e) => setUseCredit(e.target.checked)} data-testid="checkout-use-credit-checkbox" />
        </label>
      )}
      <div className="space-y-1 border-t border-slate-100 pt-3 text-sm">
        <div className="flex justify-between"><span>{tx("Totaal")}</span><span>{money(booking.amount, booking.currency)}</span></div>
        {applied > 0 && <div className="flex justify-between text-emerald-700"><span>{tx("Lestegoed")}</span><span>− {money(applied, booking.currency)}</span></div>}
        <div className="flex justify-between text-lg font-extrabold text-osk-navy"><span>{tx("Te betalen")}</span><span data-testid="checkout-amount-due">{money(booking.amount - applied, booking.currency)}</span></div>
      </div>
      <button className="osk-btn-primary w-full" disabled={busy || expired} onClick={() => onPay(useCredit)} data-testid="checkout-pay-btn">
        {booking.amount - applied > 0 ? tx("Betalen met Sentoo") : tx("Bevestigen met lestegoed")}
      </button>
      <div className="flex items-center gap-3 text-xs text-osk-muted"><SentooBadge /><span>{tx("Veilige lokale betaling. Je plek is pas definitief na een bevestigde betaling.")}</span></div>
    </div>
  );
};

export function Checkout() {
  const { id } = useParams();
  const { lang } = useI18n();
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/parent/bookings/${id}`).then((r) => setB(r.data)).catch((e) => { toast.error(errMsg(e)); setB(false); }); }, [id]);
  const left = useCountdown(b?.status === "held" ? b.hold_expires_at : null);
  if (b === null) return <Spinner />;
  if (b === false) return <div className="osk-container py-20 text-center">{tx("Boeking niet gevonden.")}</div>;
  const expired = b.status === "held" && left <= 0;
  const pay = async (useCredit) => {
    setBusy(true);
    try { const { data } = await api.post(`/parent/bookings/${id}/pay`, { use_credit: useCredit }); window.location.href = data.redirect_url; }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="osk-container max-w-2xl py-10" data-testid="checkout-page">
      <h1 className="text-3xl">{tx("Afrekenen")}</h1>
      {b.status === "held" && (
        <div className={`mt-4 flex items-center gap-3 rounded-2xl p-4 ${expired ? "bg-tile-red" : "bg-tile-yellow"}`} data-testid="checkout-timer">
          <Timer className={`h-6 w-6 ${expired ? "text-osk-red" : "text-osk-orange"}`} />
          {expired ? <span className="font-bold text-osk-red">{tx("De reservering is verlopen. De plek is vrijgegeven.")}</span> :
            <span className="text-sm">{tx("Je plek wordt nog")} <b className="text-lg" data-testid="checkout-timer-value">{String(Math.floor(left / 60)).padStart(2, "0")}:{String(left % 60).padStart(2, "0")}</b> {tx("vastgehouden.")}</span>}
        </div>
      )}
      {b.status === "paid" && <div className="mt-4 rounded-2xl bg-tile-mint p-4 font-bold text-emerald-700">{tx("Deze boeking is al betaald.")}</div>}
      {["released", "cancelled"].includes(b.status) && <div className="mt-4 rounded-2xl bg-tile-red p-4 font-bold text-osk-red">{tx("Deze reservering is vrijgegeven.")}</div>}
      <div className="osk-card mt-6 space-y-5 p-6">
        <Summary b={b} lang={lang} />
        {["held", "awaiting_payment"].includes(b.status) && <PayPanel booking={b} credit={b.credit_balance} onPay={pay} busy={busy} expired={expired} />}
        {expired && <Link to={`/lessen/${b.lesson_id}`} className="osk-btn-outline w-full" data-testid="checkout-rebook-btn">{tx("Opnieuw boeken")}</Link>}
      </div>
    </div>
  );
}

export function TestPay() {
  const { paymentId } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/payments/${paymentId}`).then((r) => setP(r.data)).catch(() => setP(false)); }, [paymentId]);
  const complete = async (outcome) => {
    setBusy(true);
    try { await api.post(`/payments/test/${paymentId}/complete`, { outcome }); nav(`/betalen/resultaat/${paymentId}`); }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  if (p === null) return <Spinner />;
  if (!p) return <div className="osk-container py-20 text-center">{tx("Betaling niet gevonden.")}</div>;
  return (
    <div className="flex min-h-[70vh] items-center justify-center bg-osk-light px-5 py-12" data-testid="test-pay-page">
      <div className="osk-card w-full max-w-md space-y-5 p-8 text-center">
        <span className="osk-pill mx-auto bg-osk-orange text-white"><FlaskConical className="mr-1 h-3.5 w-3.5" />{tx("TESTMODUS")}</span>
        <SentooBadge />
        <h1 className="text-2xl">{tx("Sentoo betaling (gesimuleerd)")}</h1>
        <p className="text-sm text-osk-text">{tx("Er zijn nog geen Sentoo-gegevens ingesteld. Kies hieronder de uitkomst van deze testbetaling.")}</p>
        <p className="text-3xl font-extrabold text-osk-navy" data-testid="test-pay-amount">{money(p.amount, p.currency)}</p>
        <p className="text-sm text-osk-muted">{p.lesson?.title}</p>
        {p.status === "pending" ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <button className="osk-btn-primary bg-emerald-600" style={{ background: "#16a34a" }} disabled={busy} onClick={() => complete("success")} data-testid="test-pay-success-btn"><CheckCircle2 className="h-5 w-5" />{tx("Betaling gelukt")}</button>
            <button className="osk-btn border-2 border-osk-red px-6 py-3 text-osk-red hover:bg-tile-red" disabled={busy} onClick={() => complete("failed")} data-testid="test-pay-fail-btn"><XCircle className="h-5 w-5" />{tx("Betaling mislukt")}</button>
          </div>
        ) : <Link to={`/betalen/resultaat/${paymentId}`} className="osk-btn-primary">{tx("Bekijk resultaat")}</Link>}
      </div>
    </div>
  );
}

export function PaymentResult() {
  const { paymentId } = useParams();
  const { user, ready } = useAuth();
  const [p, setP] = useState(null);
  useEffect(() => {
    if (!user) return;
    let n = 0;
    const load = () => api.get(`/payments/${paymentId}`).then((r) => { setP(r.data); if (r.data.status === "pending" && n++ < 10) setTimeout(load, 3000); }).catch(() => setP(false));
    load();
  }, [paymentId, user]);
  if (!ready) return <Spinner />;
  if (!user || p === false) return (
    <div className="flex min-h-[70vh] items-center justify-center px-5 py-12" data-testid="payment-result-page">
      <div className="osk-card osk-rise w-full max-w-md space-y-4 p-8 text-center" data-testid="payment-received-login-required">
        <CheckCircle2 className="mx-auto h-14 w-14 text-osk-blue" />
        <p className="text-lg font-bold text-osk-navy" data-testid="payment-received-message">
          {!user ? tx("Betaling ontvangen, log in om je boeking te bekijken.") : tx("Je hebt geen toegang tot deze boeking.")}
        </p>
        {!user && <Link to={`/inloggen?next=${encodeURIComponent(`/betalen/resultaat/${paymentId}`)}`} className="osk-btn-primary w-full" data-testid="payment-result-login-btn">{tx("Inloggen")}</Link>}
      </div>
    </div>
  );
  if (!p) return <Spinner />;
  const ok = p.status === "succeeded";
  const failed = p.status === "failed";
  return (
    <div className="flex min-h-[70vh] items-center justify-center px-5 py-12" data-testid="payment-result-page">
      <div className="osk-card osk-rise w-full max-w-md space-y-4 p-8 text-center">
        {ok ? <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-600" /> : failed ? <XCircle className="mx-auto h-16 w-16 text-osk-red" /> : <Spinner />}
        <h1 className="text-2xl" data-testid="payment-result-title">{ok ? tx("Betaling gelukt!") : failed ? tx("Betaling mislukt") : tx("Betaling wordt verwerkt…")}</h1>
        {p.lesson && <p className="text-sm text-osk-text" data-testid="payment-result-summary"><b>{p.lesson.title}</b> · {money(p.booking?.amount, p.currency)}</p>}
        {ok && p.booking?.status === "paid" && <p className="text-sm text-osk-text">{tx("De plek is gereserveerd. Je ontvangt een bevestiging en betaalbewijs per e-mail.")}</p>}
        {ok && p.booking?.status === "released" && <p className="text-sm text-osk-red">{tx("De plek was niet meer beschikbaar. Het bedrag is als lestegoed op je account gezet.")}</p>}
        {failed && <p className="text-sm text-osk-text">{tx("Er is niets afgeschreven en er is geen plek gereserveerd. Je kunt het opnieuw proberen zolang de reservering geldig is.")}</p>}
        {ok && <Link to="/ouder?tab=bookings" className="osk-btn-primary w-full" data-testid="payment-result-dashboard-btn"><ShieldCheck className="h-5 w-5" />{tx("Naar mijn boekingen")}</Link>}
        {failed && <Link to={p.booking?.pay_token ? `/betaallink/${p.booking.pay_token}` : `/boeken/${p.booking_id}`} className="osk-btn-primary w-full" data-testid="payment-result-retry-btn">{tx("Opnieuw proberen")}</Link>}
      </div>
    </div>
  );
}

export function PayLink() {
  const { token } = useParams();
  const { lang } = useI18n();
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/paylink/${token}`).then((r) => setB(r.data)).catch(() => setB(false)); }, [token]);
  if (b === null) return <Spinner />;
  if (!b) return <div className="osk-container py-20 text-center">{tx("Deze betaallink is ongeldig.")}</div>;
  const pay = async (useCredit) => {
    setBusy(true);
    try { const { data } = await api.post(`/paylink/${token}/pay`, { use_credit: useCredit }); window.location.href = data.redirect_url; }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="osk-container max-w-2xl py-10" data-testid="paylink-page">
      <h1 className="text-3xl">{tx("Maandbetaling")}</h1>
      <p className="mt-2 text-sm text-osk-text">{tx("Betaal vóór de eerste les van de maand. Onbetaalde plekken worden bij de eerste les automatisch vrijgegeven.")}</p>
      <div className="osk-card mt-6 space-y-5 p-6">
        <Summary b={b} lang={lang} />
        {b.status === "awaiting_payment" ? <PayPanel booking={b} credit={0} onPay={pay} busy={busy} /> :
          <p className="rounded-xl bg-osk-light p-3 text-sm font-bold" data-testid="paylink-status">{tx(b.status === "paid" ? "Status: betaald" : "Status: niet meer te betalen")}</p>}
      </div>
    </div>
  );
}
